'use client';

import { FC, useMemo } from 'react';

import { OrderMapperUtils } from '@ravago/shared/next-data-access';
import { getOrders } from '@ravago/shared/next-data-access/client';
import { DisplayOrder, MappedOrders, OrderBaseUrls } from '@ravago/shared/page-data/models/elements';
import { Address } from '@ravago/shared/page-data/models/elements/address.model';
import { RendererGlobals } from '@ravago/shared/page-data/models/utils';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Spinner } from '@ravago/shared/radiance/components/spinner/components/Spinner/Spinner';
import { TabProps } from '@ravago/shared/radiance/components/tabs/components/Tab/Tab';
import { Tabs } from '@ravago/shared/radiance/components/tabs/components/Tabs/Tabs';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { useQuery } from '@tanstack/react-query';

import { parseAsArrayOf, parseAsBoolean, parseAsInteger, parseAsString, useQueryState, useQueryStates } from 'nuqs';

import { OrderFilterUtils } from '../../../../utils/order/order-filters.utils';
import OrderNotFoundIllustration from '../../../illustrations/order-not-found-illustration';
import { EmptyState } from '../../../shared/empty-state/empty-state.component';
import { OrderLinesTable } from '../../../shared/order-lines-table/order-lines-table';
import { OrderShipments } from '../../../shared/order/order-shipments';
import { OrderFilters } from '../models/order-filters';

import { OrderOverviewSearchFilter } from './order-overview-search-filter';

export type Props = {
   globals: RendererGlobals;
   orders: MappedOrders;
   hasCallOffs: boolean;
   usesAgreementApp: boolean;
   isUSBusinessContext: boolean;
   orderBaseUrls: OrderBaseUrls;
};

export const OrderOverview: FC<Props> = ({
   globals: {
      locale,
      acceptWebp,
      isConsumer,
      actions: { requestCoa, requestDeliveryNote, requestBol, downloadDocument }
   },
   orderBaseUrls,
   orders,
   hasCallOffs,
   usesAgreementApp,
   isUSBusinessContext
}) => {
   const translate = useTranslations();

   const closedOrdersQuery = useQuery({
      queryKey: ['open-orders'],
      queryFn: () => getOrders(false),
      select: (data) => {
         const mappedClosedOrders = OrderMapperUtils.getMappedOrders(data);
         return mappedClosedOrders.closedOrderLines;
      }
   });

   const [filters, setFilters] = useQueryStates({
      search: parseAsString.withDefault(''),
      deliveryDateFrom: parseAsString.withDefault(''),
      deliveryDateTo: parseAsString.withDefault(''),
      deliveryAddressIds: parseAsArrayOf(parseAsInteger).withDefault([]),
      callOff: parseAsBoolean.withDefault(false)
   });
   const isFiltered = useMemo(() => {
      const { search, deliveryDateFrom, deliveryDateTo, deliveryAddressIds, callOff } = filters;
      return search.length > 0 || deliveryDateFrom.length > 0 || deliveryDateTo.length > 0 || deliveryAddressIds.length > 0 || callOff;
   }, [filters]);

   const [activeTab, setActiveTab] = useQueryState('tab', parseAsInteger.withDefault(0));
   const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));

   const filteredOrders = useMemo(() => {
      const searchOpenOrders = OrderFilterUtils.onSearch(filters.search, orders.openOrderLines);
      const filteredOpenOrders = OrderFilterUtils.onFilter(filters, searchOpenOrders);

      let filteredClosedOrders: DisplayOrder[] = [];
      if (closedOrdersQuery.data) {
         const searchCloseOrders = OrderFilterUtils.onSearch(filters.search, closedOrdersQuery.data);
         filteredClosedOrders = OrderFilterUtils.onFilter(filters, searchCloseOrders);
      }

      const filteredInTransitShipmentsOrderLines = OrderFilterUtils.onFilterShipmentOrders(orders.inTransitShipments, filters);
      const filteredTransportArrangedShipmentsOrderLines = OrderFilterUtils.onFilterShipmentOrders(orders.transportArrangedShipments, filters);
      return {
         closedOrderLines: filteredClosedOrders,
         openOrderLines: filteredOpenOrders,
         inTransitShipments: filteredInTransitShipmentsOrderLines,
         transportArrangedShipments: filteredTransportArrangedShipmentsOrderLines
      };
   }, [orders, closedOrdersQuery.data, filters]);

   const tabs = useMemo(() => {
      const baseTabs: Array<TabProps> = [
         {
            label: `${translate('my-orders.tab.open')} (${
               isFiltered && filteredOrders ? filteredOrders.openOrderLines.length : orders.openOrderLines.length
            })`,
            id: 'open-orders-tab'
         },
         {
            label: `${
               closedOrdersQuery.isLoading
                  ? `${translate('my-orders.tab.closed')} (...)`
                  : `${translate('my-orders.tab.closed')} (${filteredOrders.closedOrderLines.length})`
            }`,
            id: 'closed-orders-tab'
         }
      ];

      return baseTabs;
   }, [filteredOrders, closedOrdersQuery.isLoading]);

   const addresses = useMemo(() => {
      const allOrders = [...orders.openOrderLines];
      if (closedOrdersQuery.data) {
         allOrders.push(...closedOrdersQuery.data);
      }
      return allOrders.filter((order) => order.deliveryTermAddress?.id).map((order) => order.deliveryTermAddress as Address);
   }, [orders, closedOrdersQuery.data]);

   const handleSearchOrFilter = (value: OrderFilters) => {
      setFilters(value);
      setPage(1);
   };

   return (
      <Box direction="column">
         <Box direction="column">
            <OrderOverviewSearchFilter
               filters={filters}
               onSearchOrFilter={handleSearchOrFilter}
               orderAddresses={addresses}
               hasCallOffs={hasCallOffs}
               disabled={false}
            />

            {(orders.inTransitShipments.length > 0 || orders.transportArrangedShipments.length > 0) && (
               <OrderShipments
                  isConsumer={isConsumer}
                  locale={locale}
                  acceptWebp={acceptWebp}
                  orderDetail={filteredOrders}
                  orderBaseUrls={orderBaseUrls}
                  openedFrom="order_overview_shipment"
                  requestBol={requestBol}
                  requestDeliveryNote={requestDeliveryNote}
                  requestCoa={requestCoa}
                  downloadDocument={downloadDocument}
                  isUSBusinessContext={isUSBusinessContext}
               />
            )}

            {isFiltered && filteredOrders?.transportArrangedShipments.length === 0 && filteredOrders?.inTransitShipments.length === 0 && (
               <Typography component="p" variant="body" align="center">
                  {translate('my-orders.order-overview-empty-transport-orders')}
               </Typography>
            )}

            <Box direction="column" spacing={{ top: 'md' }}>
               <Tabs
                  tabs={tabs}
                  activeTab={activeTab}
                  onTabChange={(index) => {
                     setActiveTab(index);
                     setPage(1);
                  }}
               />
            </Box>
         </Box>

         {activeTab === 0 && (
            <>
               {(orders.openOrderLines.length > 0 || (filteredOrders && filteredOrders.openOrderLines.length > 0)) && (
                  <OrderLinesTable
                     orderLines={filteredOrders.openOrderLines}
                     isConsumer={isConsumer}
                     acceptWebp={acceptWebp}
                     locale={locale}
                     itemsPerPage={10}
                     page={page}
                     orderBaseUrls={orderBaseUrls}
                     openedFrom="order_overview_order"
                     usesAgreementApp={usesAgreementApp}
                     onPageChange={setPage}
                     requestCoa={requestCoa}
                     requestBol={requestBol}
                     requestDeliveryNote={requestDeliveryNote}
                     downloadDocument={downloadDocument}
                     isUSBusinessContext={isUSBusinessContext}
                  />
               )}
               {(orders.openOrderLines.length === 0 || (filteredOrders?.openOrderLines.length === 0 && isFiltered)) && (
                  <EmptyState
                     label={translate('my-orders.no-open-orders-intro')}
                     image={<OrderNotFoundIllustration />}
                     title={translate('my-orders.no-open-orders')}
                  />
               )}
            </>
         )}

         {activeTab === 1 && (
            <>
               {closedOrdersQuery.isLoading && (
                  <Box direction="column" items="center">
                     <Spinner />
                  </Box>
               )}
               {closedOrdersQuery.data && closedOrdersQuery.data.length > 0 && (
                  <OrderLinesTable
                     orderLines={filteredOrders.closedOrderLines}
                     isConsumer={isConsumer}
                     acceptWebp={acceptWebp}
                     locale={locale}
                     orderBaseUrls={orderBaseUrls}
                     itemsPerPage={10}
                     page={page}
                     openedFrom="order_overview_order"
                     usesAgreementApp={usesAgreementApp}
                     onPageChange={setPage}
                     requestCoa={requestCoa}
                     requestBol={requestBol}
                     requestDeliveryNote={requestDeliveryNote}
                     downloadDocument={downloadDocument}
                     isUSBusinessContext={isUSBusinessContext}
                  />
               )}
               {(!closedOrdersQuery.isLoading && closedOrdersQuery.data && closedOrdersQuery.data.length === 0) ||
                  (!closedOrdersQuery.isLoading && filteredOrders?.closedOrderLines.length === 0 && isFiltered && (
                     <EmptyState
                        label={translate('my-orders.no-open-orders-intro')}
                        image={<OrderNotFoundIllustration />}
                        title={translate('my-orders.no-closed-orders')}
                     />
                  ))}
            </>
         )}
      </Box>
   );
};
